import { IResponseGetCourseTemplates } from '../interfaces/response-get-course-templates.interface';
import { CourseTemplate } from '../models/course-template.model';
import { ResponseCourseTemplates } from '../models/response-course-templates.model';
import { COURSE_TEMPLATES_API, PURCHASE_TYPES } from '../values/course-templates.values';
import ToolService from '@/providers/tools';
import { RequestProvider } from '@/shared/providers/request.provider';
import { TEMPLATE_SCOPE } from '@/course-templates/values/course-templates.values';
import Moment from 'moment';

class CourseTemplateServiceClass {

  async createCourseTemplate(courseId: number, isPublic: boolean) : Promise<void> {
    let templateScope = isPublic ? TEMPLATE_SCOPE.PUBLIC : TEMPLATE_SCOPE.ORGANIZATION;
    const req = COURSE_TEMPLATES_API.CREATE_TEMPLATE_FROM_COURSE
      .replace('{sourceContentId}', String(courseId))
      .replace('{templateScope}', String(templateScope));
    return RequestProvider.post(req);
  }
  
  async getCourseTemplates(page: number = 0, pageSize = 10, scope = null) : Promise<IResponseGetCourseTemplates> {
    const req = COURSE_TEMPLATES_API.GET_COURSE_TEMPLATES
      .replace('{page}', String(page))
      .replace('{pageSize}', String(pageSize));
    const data = await RequestProvider.get(req, {scope});
    const courseTemplates = ToolService.reCastObjects(data.content, ResponseCourseTemplates, CourseTemplate);
    return {
      courseTemplates,
      totalPages: data.totalPages
    };
  }

  createCourseFromTemplate(courseTemplateId: number) : Promise<void> {
    const req = COURSE_TEMPLATES_API.CREATE_COURSE_FROM_TEMPLATE
      .replace('{courseTemplateId}', String(courseTemplateId));
    return RequestProvider.post(req);
  }

  async createNewCourseTemplate(templateRequest: CourseTemplate, image: any) : Promise<any> {
    let data = new FormData();
    data.append('templateRequest', JSON.stringify(templateRequest));
    data.append('coverImage', image);
    return RequestProvider.post(COURSE_TEMPLATES_API.CREATE_COURSE_TEMPLATE, data, {}, { 'Content-Type': 'multipart/form-data' });
  }

  async updateCourseTemplate(templateRequest: any, image: any, templateId: number) : Promise<any> {
    let req = COURSE_TEMPLATES_API.UPDATE_COURSE_TEMPLATE
      .replace('{templateId}', String(templateId));
    let data = new FormData();
    data.append('templateRequest', JSON.stringify(templateRequest));
    data.append('coverImage', image);
    return RequestProvider.put(req, data, {}, { 'Content-Type': 'multipart/form-data' });
  }

  async getGradeLevels(orgId: number) : Promise<any>  {
    const req = COURSE_TEMPLATES_API.GET_GRADE_LEVELS
      .replace('{organizationId}', String(orgId));
    return await RequestProvider.get(req);
  }

  async getCourseTemplate(templateId: number): Promise<any>  {
    const req = COURSE_TEMPLATES_API.GET_COURSE_TEMPLATE
      .replace('{templateId}', String(templateId));

    return await RequestProvider.get(req);
  }

  async getSchoolCalendarOpt(orgId) {
    const req = COURSE_TEMPLATES_API.GET_SCHOOL_CALENDAR_OPT;
    let startDate : string = Moment().format('YYYY-MM-DD');
    let endDate   : string = Moment().add(3, 'years').format('YYYY-MM-DD');
    return await RequestProvider.get(req, {organizationId : orgId, includeDates: false, startDate, endDate});
  }

  async getMyCourseTemplates(page: number = 0, pageSize = 10, purchaseTypes = null, gradeLevelIds = null): Promise<IResponseGetCourseTemplates>  {
    const req = COURSE_TEMPLATES_API.GET_MY_COURSE_TEMPLATES;
    if(!purchaseTypes) pageSize = 10000;
    let data = await RequestProvider.get(req, {page, pageSize, purchaseTypes, gradeLevelIds});
    const courseTemplates = ToolService.reCastObjects(data.content, ResponseCourseTemplates, CourseTemplate);
    return {
      courseTemplates,
      totalPages: data.totalPages
    };
  }

  async publishCourseTemplate(templateId, templateScope) {
    const req = COURSE_TEMPLATES_API.PUBLISH_COURSE_TEMPLATE
    .replace('{templateId}', String(templateId))
    .replace('{templateScope}', String(templateScope));
    return await RequestProvider.put(req);
  }

  async buyCourseTemplate(templateId) {
    const req = COURSE_TEMPLATES_API.BUY_COURSE_TEMPLATE
    .replace('{templateId}', String(templateId));
    return await RequestProvider.post(req);
  }

  async removeCourseTemplate(templateId: number) {
    const req = COURSE_TEMPLATES_API.REMOVE_COURSE_TEMPLATE
      .replace('{templateId}', String(templateId))
    return await RequestProvider.remove(req);
  }
  
  async lockCourseTemplate(templateId: number) {
    const req = COURSE_TEMPLATES_API.LOCK_UNLOCK_TEMPLATE
      .replace('{templateId}', String(templateId));
    return await RequestProvider.put(req);
  }

  async unlockCourseTemplate(templateId: number) {
    const req = COURSE_TEMPLATES_API.LOCK_UNLOCK_TEMPLATE
      .replace('{templateId}', String(templateId));
    return await RequestProvider.remove(req);
  }

  async shareCourseTemplate(templateId: number, personId: number) {
    const req = COURSE_TEMPLATES_API.SHARE_UNSHARE_TEMPLATE
      .replace('{templateId}', String(templateId))
      .replace('{personId}', String(personId));
    return await RequestProvider.put(req);
  }

  async unshareCourseTemplate(templateId: number, personId: number) {
    const req = COURSE_TEMPLATES_API.SHARE_UNSHARE_TEMPLATE
      .replace('{templateId}', String(templateId))
      .replace('{personId}', String(personId));
    return await RequestProvider.remove(req);
  }

  async updateOwnerTemplate(templateId: number, personId: number) {
    const req = COURSE_TEMPLATES_API.UPDATE_OWNER_TEMPLATE
      .replace('{templateId}', String(templateId))
      .replace('{personId}', String(personId));
    return await RequestProvider.post(req);
  }

  async getCourseTemplateById(templateId: number) {
    const req = COURSE_TEMPLATES_API.GET_TEMPLATE_INFO
      .replace('{templateId}', String(templateId));
    return await RequestProvider.get(req);
  }

  getQuickSubjectInfo(quickSubjectId: number) {
    const req = COURSE_TEMPLATES_API.GET_QUICK_SUBJECT_INFO
      .replace('{id}', String(quickSubjectId));

    return RequestProvider.get(req);
  }

  async copyToSubject(templateId: number, sync: boolean) {
    const req = COURSE_TEMPLATES_API.COPY_TO_SUBJECT
      .replace('{templateId}', String(templateId))
      .replace('{sync}', String(sync));

    return RequestProvider.post(req);
  }

  async getCourseTemplatesByIds(templateIds: number[]): Promise<any>  {
    const req = COURSE_TEMPLATES_API.GET_COURSE_TEMPLATES_STRUCTURE_BY_IDS
      .replace('{templateIds}', String(templateIds.join(',')));
    return await RequestProvider.get(req);
  }
}

export const CourseTemplateService = new CourseTemplateServiceClass();
